/* =====================================================================
 * useAdminDashboardAccess — 어드민 대시보드 노출 여부.
 *
 * 어드민 대시보드는 role 이 아니라 서버의 부서코드 허용목록이 정하므로
 * `usePermission` 으로 판정할 수 없다. `/access` 는 거부도 200 으로
 * 답하기 때문에 만료된 세션과 구분되며, 실패하면 항목을 숨긴다 —
 * 링크를 보여주고 403 을 내는 편보다 낫다. 이 게이트는 표시용일 뿐이고
 * 실제 차단은 서버가 한다.
 *
 * 사이드바 "관리" 섹션과 설정 > 배경화면 적용 페이지 목록이 같은 판정을
 * 공유한다. queryKey / staleTime 이 갈라지면 한쪽에만 항목이 뜨거나 요청이
 * 두 번 나가므로 훅 하나로 묶는다.
 * ===================================================================== */

import { useQuery } from '@tanstack/react-query';
import { fetchAdminDashboardAccess } from '@auw/api-client';
import { useFeatureFlags } from '@auw/feature-flags';
import { useUserStore } from '@auw/stores';

export interface AdminDashboardAccessState {
  /** 테넌트가 `mode.admin-dashboard` 를 켰는지. */
  readonly modeEnabled: boolean;
  /** 플래그 + 서버 허용목록을 모두 통과했는지. */
  readonly canView: boolean;
}

export function useAdminDashboardAccess(): AdminDashboardAccessState {
  const { isEnabled } = useFeatureFlags();
  const user = useUserStore((s) => s.user);
  const modeEnabled = isEnabled('mode.admin-dashboard');

  const { data } = useQuery({
    queryKey: ['adminDashboard', 'access', user?.userId],
    queryFn: fetchAdminDashboardAccess,
    enabled: modeEnabled && !!user,
    staleTime: 5 * 60_000,
    retry: false,
  });

  return { modeEnabled, canView: modeEnabled && !!user && data?.allowed === true };
}
